import { Link, useNavigate } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { getLoginUrl } from '../../../helpers/authConfig'
import styles from './styles.module.scss'

// Shared types, formatters and helpers for the Sentinel-admin "Bundle Access
// Log" pages (batch overview -> per-batch accesses -> single access).

export const UNASSIGNED_BATCH = 'unassigned'
export const ACCESS_LOG_BASE = '/admin/document-access'
export const ACCESS_KEYS_PATH = `${ACCESS_LOG_BASE}/keys`

export type AccessMode = 'raw' | 'viewer'
export type AccessKeyType = 'none' | 'master' | 'alias' | 'dashboard'
export type Outcome =
    | 'started'
    | 'success'
    | 'bad_rawkey'
    | 'key_disabled'
    | 'key_expired'
    | 'not_authorised'
    | 'disabled'
    | 'not_found'
    | 's3_error'
    | 'client_aborted'
    | 'error'

export interface ParsedUa {
    browser: string | null
    os: string | null
    device: string | null
}

export interface AccessRow {
    id: number
    accessed_at: string
    completed_at: string | null
    access_mode: AccessMode
    outcome: Outcome
    http_status: number | null
    file_name: string | null
    case_ref: string | null
    batch_group: string | null
    funder_client_id: string | null
    ip_address: string | null
    user_agent: string | null
    geo_country: string | null
    geo_region: string | null
    geo_city: string | null
    content_length: number | null
    bytes_sent: number | null
    duration_ms: number | null
    error_message: string | null
    batch_introducer: string | null
    access_key_type: AccessKeyType | null
    access_key_id: number | null
    access_key_label: string | null
    access_key_holder: string | null
    access_key_organisation: string | null
    access_key_display: string | null
    ua: ParsedUa
    location: string | null
}

export interface AccessKeyInfo {
    id: number
    label: string | null
    holderName: string | null
    holderEmail: string | null
    organisation: string | null
    enabled: boolean
    systemRole: 'dashboard' | 'master' | null
}

export interface AccessDetail extends AccessRow {
    doc_type: string
    locator: string
    s3_key: string
    referer: string | null
    query_string: string | null
    geo_timezone: string | null
    geo_lat: number | string | null
    geo_lng: number | string | null
    range_header: string | null
    batch_documents_disabled: number | null
}

export interface BatchSummary {
    batch_key: string
    batch_group: string | null
    introducer: string | null
    documents_disabled: boolean
    total_accesses: number
    raw_success: number
    raw_failures: number
    raw_in_progress: number
    viewer_accesses: number
    unique_ips: number
    unique_bundles: number
    unique_keys: number
    first_access: string | null
    last_access: string | null
}

export const OUTCOME_LABELS: Record<Outcome, string> = {
    started: 'In progress',
    success: 'Success',
    bad_rawkey: 'Bad raw key',
    key_disabled: 'Key disabled',
    key_expired: 'Key expired',
    not_authorised: 'Not authorised',
    disabled: 'Batch disabled',
    not_found: 'Not found',
    s3_error: 'S3 error',
    client_aborted: 'Client aborted',
    error: 'Error',
}

export const OUTCOME_OPTIONS: { value: string; label: string }[] = [
    { value: 'all', label: 'All outcomes' },
    { value: 'success', label: 'Success' },
    { value: 'failures', label: 'All failures' },
    ...(Object.keys(OUTCOME_LABELS) as Outcome[])
        .filter((o) => o !== 'success')
        .map((o) => ({ value: o, label: OUTCOME_LABELS[o] })),
]

export const outcomeTone = (outcome: Outcome): 'ok' | 'warn' | 'bad' | 'muted' => {
    if (outcome === 'success') return 'ok'
    if (outcome === 'started') return 'muted'
    if (outcome === 'client_aborted' || outcome === 'disabled') return 'warn'
    return 'bad'
}

/** Flat back link matching the omnibus pages (replaces the gradient BackButton). */
export const BackLink = ({ to, text }: { to: string; text: string }) => {
    const navigate = useNavigate()
    return (
        <button type="button" className={styles.backLink} onClick={() => navigate(to)}>
            <FaArrowLeft /> {text}
        </button>
    )
}

export type PillTone = 'ok' | 'warn' | 'bad' | 'muted' | 'blue' | 'sky' | 'default'

export const Pill = ({ tone = 'default', children }: { tone?: PillTone; children: React.ReactNode }) => (
    <span className={`${styles.pill} ${tone === 'default' ? '' : styles[tone]}`}>{children}</span>
)

export const OutcomeBadge = ({ outcome }: { outcome: Outcome }) => (
    <Pill tone={outcomeTone(outcome)}>{OUTCOME_LABELS[outcome] ?? outcome}</Pill>
)

/** "Master key" / "Label (Holder)" / "—" with a link to the keys page for aliases. */
export const KeyCell = ({ row }: { row: Pick<AccessRow, 'access_key_type' | 'access_key_id' | 'access_key_display'> }) => {
    if (row.access_key_type === 'master') {
        return (
            <Link className={styles.rowLink} to={ACCESS_KEYS_PATH} onClick={(e) => e.stopPropagation()}>
                <Pill tone="warn">Master key</Pill>
            </Link>
        )
    }
    if (row.access_key_type === 'dashboard') {
        return (
            <Link className={styles.rowLink} to={ACCESS_KEYS_PATH} onClick={(e) => e.stopPropagation()}>
                <Pill tone="blue">Dashboard</Pill>
            </Link>
        )
    }
    if (row.access_key_type === 'alias') {
        return (
            <Link className={styles.rowLink} to={ACCESS_KEYS_PATH} onClick={(e) => e.stopPropagation()}>
                {row.access_key_display || `Key #${row.access_key_id}`}
            </Link>
        )
    }
    return <span className={styles.muted}>—</span>
}

// Raw downloads are the ones worth watching, so they take the faded blue;
// viewer opens stay in neutral slate.
export const ModeBadge = ({ mode }: { mode: AccessMode }) => (
    <Pill tone={mode === 'raw' ? 'blue' : 'muted'}>{mode === 'raw' ? 'Raw' : 'Viewer'}</Pill>
)

export const formatDateTime = (raw: string | null | undefined) => {
    if (!raw) return '—'
    try {
        return new Date(raw).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        })
    } catch {
        return raw
    }
}

export const formatBytes = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') return '—'
    const n = Number(value)
    if (!Number.isFinite(n)) return '—'
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
    return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

export const formatDuration = (ms: number | null | undefined) => {
    if (ms === null || ms === undefined) return '—'
    if (ms < 1000) return `${ms} ms`
    return `${(ms / 1000).toFixed(2)} s`
}

export const formatUa = (ua: ParsedUa | undefined) => {
    if (!ua) return '—'
    return [ua.browser, ua.os].filter(Boolean).join(' · ') || '—'
}

export const batchLabel = (batchGroup: string | null | undefined) => batchGroup ?? 'Unassigned'

export const batchKey = (batchGroup: string | null | undefined) => batchGroup ?? UNASSIGNED_BATCH

export const batchAccessPath = (batchGroup: string | null | undefined) =>
    `${ACCESS_LOG_BASE}/batch/${encodeURIComponent(batchKey(batchGroup))}`

export const accessDetailPath = (id: number) => `${ACCESS_LOG_BASE}/access/${id}`

/**
 * Consistent handling of auth failures from the admin endpoints. Returns true
 * when the error was handled (caller should stop), false otherwise.
 */
export const handleAdminApiError = (error: any, navigate: (path: string) => void): boolean => {
    const status = error?.response?.status
    if (status === 401) {
        Report.failure('Session Timed Out', 'Please log in again to continue.', 'Okay', () => {
            window.location.href = getLoginUrl()
        })
        return true
    }
    if (status === 403) {
        Report.failure(
            'Access Denied',
            'The Bundle Access Log is restricted to Sentinel admin users only.',
            'Return to Dashboard',
            () => navigate('/home')
        )
        return true
    }
    return false
}
